/**
 * Modal root, backdrop, panel, drag handle, sheet header rows, close control.
 * Used by: AuthSheetHost (panel styles via `ConfigurableOverlay`), DailyCustomizationModal (`src/version1/menu/components/DailyCustomizationModal.tsx`; v2/v3 parity under `version2|3/menu/…`).
 * @see ../atomicRules.ts
 */
import { StyleSheet, type ViewStyle } from 'react-native';
import { appTheme } from '../../appTheme';
import type { ThemeColors } from '../../store';

/** Hit target + visual diameter for sheet close. */
export const AUTH_SHEET_CLOSE_SIZE = 36;
/** Inset from panel corner — clears the rounded radius so the control does not hug the edge. */
export const AUTH_SHEET_CLOSE_INSET = appTheme.spacing.md;

/** Hairline footer divider with breathing room above/below the rule. Used by: v3 calendar sheets, date pickers. */
export function createSheetFooterDividerStyle(c: ThemeColors): ViewStyle {
  return {
    marginTop: appTheme.modal.footerMarginTop,
    paddingTop: appTheme.modal.footerPaddingTop,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: c.borderLight ?? c.border,
  };
}

export function createBottomSheetChromeStyles(c: ThemeColors) {
  const close_fill = c.surfaceLight ?? c.surface;

  return StyleSheet.create({
    modalRoot: {
      flex: 1,
      justifyContent: 'flex-end',
    },
    backdrop: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: c.backdrop,
    },
    panel: {
      // Overlay owns pixel `maxHeight` — never `%` (unbounded / wrong parent → form collapses).
      width: '100%',
      backgroundColor: c.surface,
      borderTopLeftRadius: appTheme.modal.sheetBorderRadius,
      borderTopRightRadius: appTheme.modal.sheetBorderRadius,
      overflow: 'hidden',
      ...appTheme.shadows.lg,
      shadowColor: c.shadowColor,
    },
    handle: {
      width: 40,
      height: 4,
      borderRadius: 2,
      backgroundColor: c.border,
      alignSelf: 'center',
      marginTop: appTheme.spacing.sm,
      marginBottom: appTheme.spacing.xs,
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: appTheme.spacing.md,
      paddingTop: appTheme.spacing.sm,
      paddingBottom: appTheme.spacing.sm,
      minHeight: 44,
    },
    /** OTP / reset — close only, no title band. */
    headerRowCloseOnly: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      paddingHorizontal: AUTH_SHEET_CLOSE_INSET,
      paddingTop: AUTH_SHEET_CLOSE_INSET,
      paddingBottom: appTheme.spacing.xs,
    },
    headerTitle: {
      ...appTheme.typography.h3,
      color: c.text,
      flex: 1,
      textAlign: 'center',
    },
    headerSpacer: {
      width: AUTH_SHEET_CLOSE_SIZE,
      height: AUTH_SHEET_CLOSE_SIZE,
    },
    headerFlexFill: {
      flex: 1,
    },
    /**
     * Floating close for center-card login/register — no header row.
     * Parent must be `position: 'relative'` (see AuthSheetHost body wrap).
     */
    closeButtonFloating: {
      position: 'absolute',
      top: AUTH_SHEET_CLOSE_INSET,
      right: AUTH_SHEET_CLOSE_INSET,
      zIndex: 20,
      elevation: 8,
    },
    /** Hit target for sheet close — icon must be optically centered in this box. */
    closeButton: {
      width: AUTH_SHEET_CLOSE_SIZE,
      height: AUTH_SHEET_CLOSE_SIZE,
      borderRadius: AUTH_SHEET_CLOSE_SIZE / 2,
      backgroundColor: close_fill,
      justifyContent: 'center',
      alignItems: 'center',
      overflow: 'hidden',
    },
    /** Absolute fill so SVG layout quirks cannot shift the glyph off-center. */
    closeButtonIconWrap: {
      ...StyleSheet.absoluteFillObject,
      justifyContent: 'center',
      alignItems: 'center',
    },
  });
}

export type BottomSheetChromeStyles = ReturnType<typeof createBottomSheetChromeStyles>;
